/**
 * The quote → contract → project flow (docs/specs/quotes/overview.md §3; TJR inventory 01 §10.5):
 * new quote with tenant defaults, areas from imported flight-plan files, the checks before a revision is sent
 * (TJR's print checks that still make sense, AC-Q5), the contract snapshot and the project set-up from a signed
 * contract.
 */
import type { Uuid, ValidationIssue } from './model.js';
import type { BndFile, KmlBoundary, LinesFile } from './plan-files.js';
import { areaLineKm, tjrAreaKm, tjrTieSpacing, validatePaymentSchedule } from './quote-calc.js';
import type {
  BoundaryTable,
  ClauseTemplate,
  ContractSnapshot,
  QuoteAggregate,
  QuoteArea,
  QuoteCharge,
  SamplingSpec,
  CommercialTerms,
  ChargeKind,
} from './quote-model.js';
import { CHARGE_KINDS } from './quote-model.js';
import { projectQcColumns, validateQcRules, type QcRule } from './qc-spec.js';
import { jobNumberFromQuote } from './quote-number.js';
import {
  acceptedOptionId,
  optionAggregate,
  optionName,
  optionsOf,
  validateOptions,
} from './quote-options.js';

/** TJR's base station choices (`baseStationCB`). */
export const BASE_STATIONS: readonly string[] = [
  'Geometrics G856AX proton precession magnetometer',
  'Geometrics 823B Cesium magnetometer',
];

/** TJR's formats (`magLocCB`, `magGridCB`); blank prints "the clients preferred format". */
export const LOCATED_FORMATS: readonly string[] = ['', 'ASEG GDF2', 'Geosoft', 'ASCII'];
export const GRID_FORMATS: readonly string[] = ['', 'ERMapper', 'Geosoft', 'ASCII'];

/**
 * The 43 located-data channel names of TJR (`ctrdatfls.dat` 000), offered in the channel picker; any other text can
 * be typed (the 2026 contract lists channels TJR cannot express).
 */
export const TJR_CHANNELS: readonly string[] = [
  'Air temperature (degrees Celsius)',
  'Barometric pressure (HPa)',
  'Compensated total magnetic intensity reading (nT)',
  'Corrected and levelled total count (cps)',
  'Corrected and levelled potassium (cps)',
  'Corrected and levelled uranium (cps)',
  'Corrected and levelled thorium (cps)',
  'Date (YYMMDD)',
  'Diurnal (nT)',
  'DTM',
  'Easting UTM (metres)',
  'Fiducial number',
  'Final levelled total magnetic intensity (nT)',
  'Final total count (Air Absorbed Dose Rate)',
  'Final potassium (%K)',
  'Final uranium (eU, ppm)',
  'Final thorium (eTh, ppm)',
  'First Vertical Derivative TMI (1VD)',
  'Fluxgate X (nT)',
  'Fluxgate Y (nT)',
  'Fluxgate Z (nT)',
  'GPS height (metres)',
  'IGRF (nT)',
  'Line number',
  'Live-time (msec)',
  'Northing UTM (metres)',
  'Potassium (%K)',
  'Radar altimeter height (metres)',
  'Radiometric 256 channel spectra data',
  'Raw cosmic (cps)',
  'Raw potassium (cps)',
  'Raw thorium (cps)',
  'Raw total count (cps)',
  'Raw total magnetic intensity reading (nT)',
  'Raw uranium (cps)',
  'Reduced To Pole Total Magnetic Intensity (RTP)',
  'Thorium (eTh, ppm)',
  'Tie-line levelled total magnetic intensity reading (nT)',
  'Time of reading (local time in seconds after midnight)',
  'Total Count (Air Absorbed Dose Rate)',
  'Total Magnetic Intensity (TMI)',
  'Uranium (eU, ppm)',
  'Gradient Enhanced TMI',
];

/**
 * TJR's "Set Default" channel sets (resource string 64552, the correct copy; `ctrdatfls.dat` 004 is one index off,
 * bug B14), as names: Located #1, Located #2, Located #3, Grid #1, Grid #2. Indices 0-based into TJR_CHANNELS.
 */
const DEFAULT_SETS: readonly (readonly number[])[] = [
  [23, 7, 38, 11, 10, 25, 27, 21, 18, 19, 20, 33, 2, 37, 12, 8, 22],
  [23, 7, 38, 11, 10, 25, 27, 21, 24, 1, 0, 32, 30, 34, 31, 29, 3, 4, 5, 6],
  [26, 36, 39, 41],
  [40, 17, 35],
];
export function defaultChannelSet(i: 0 | 1 | 2 | 3): string[] {
  return (DEFAULT_SETS[i] ?? []).map((n) => TJR_CHANNELS[n]!).filter(Boolean);
}

export function emptySampling(quoteId: Uuid): SamplingSpec {
  return {
    quote_id: quoteId,
    magnetometers: 1,
    mag_rate_hz: 20,
    spectrometer_packs: 0,
    upward_detector: false,
    rad_rate_hz: null,
    base_station: BASE_STATIONS[0]!,
    datum: 'GDA94',
    video: false,
    ground_concentrations: false,
    mag_located_format: '',
    rad_located_format: '',
    mag_grid_format: '',
    rad_grid_format: '',
  };
}

export function emptyTerms(quoteId: Uuid, params: ClauseTemplate['params'] = {}): CommercialTerms {
  const num = (k: string, d: number): number => {
    const v = Number(params[k]);
    return Number.isFinite(v) ? v : d;
  };
  return {
    quote_id: quoteId,
    pp1_pct: null,
    pp2_pct: null,
    balance_pct: null,
    payment_days: num('payment_days', 14),
    late_fee_pct_month: num('late_fee_pct_month', 1.5),
  };
}

export function emptyCharges(quoteId: Uuid, optionId: Uuid): QuoteCharge[] {
  return CHARGE_KINDS.map((kind: ChargeKind) => ({
    quote_id: quoteId,
    option_id: optionId,
    kind,
    amount: null,
    cap_days: null,
    note: '',
    fuel_mode: 'none' as const,
  }));
}

/**
 * A quote area from a block's flight-plan files: line km computed from the lines file (`.lns`, or the line list of a
 * `.sum`) by TJR's rule (decisions 124, 169), spacing and bearings from the file, tie spacing as TJR assumes it
 * (10 × traverse) when the file has none, boundary from the BND.
 */
export function areaFromPlanFiles(
  base: Pick<
    QuoteArea,
    'id' | 'quote_id' | 'option_id' | 'sort' | 'name' | 'sensor_height_m' | 'rate_per_line_km'
  >,
  sum: LinesFile,
  bnd: BndFile | null,
  fileName: string,
): QuoteArea {
  const km = tjrAreaKm(sum.lines);
  return {
    ...base,
    trav_spacing_m: sum.trav_spacing_m,
    trav_direction_deg: sum.trav_bearing_deg,
    tie_spacing_m: sum.tie_spacing_m ?? tjrTieSpacing(sum.trav_spacing_m),
    tie_direction_deg: sum.tie_bearing_deg ?? (sum.trav_bearing_deg + 90) % 360,
    zone: sum.zone ?? bnd?.zone ?? null,
    hemisphere: sum.hemisphere ?? 'S',
    traverse_km: km.traverse_km,
    tie_km: km.tie_km,
    line_km_override: null,
    override_reason: '',
    boundary: bnd ? bnd.boundary : [],
    boundary_table: null,
    lines_file: fileName,
  };
}

/** The printed boundary table from a `_BND.inp` (grid, in the area's zone), in the quote's boundary datum. */
export function boundaryTableFromBnd(bnd: BndFile, datum: string, source: string): BoundaryTable {
  return { datum, kind: 'grid', zone: bnd.zone, points: bnd.boundary, source };
}

/** The printed boundary table from a client's `_BND.kml`: longitude and latitude, WGS 84 (decision 170). */
export function boundaryTableFromKml(kml: KmlBoundary, source: string): BoundaryTable {
  return { datum: 'WGS84', kind: 'geographic', zone: null, points: kml.points, source };
}

/** Area checks shared by the editor and "Send". */
export function validateArea(a: QuoteArea, pricing: boolean): ValidationIssue[] {
  const f = (k: string): string => `area.${a.id}.${k}`;
  const issues: ValidationIssue[] = [];
  if (!a.name.trim()) issues.push({ field: f('name'), message: 'Give the area a name.' });
  if (!(a.trav_spacing_m > 0))
    issues.push({ field: f('trav_spacing_m'), message: 'Traverse spacing must be more than 0 m.' });
  if (!(a.tie_spacing_m > 0))
    issues.push({ field: f('tie_spacing_m'), message: 'Tie spacing must be more than 0 m.' });
  if (!(a.sensor_height_m > 0))
    issues.push({ field: f('sensor_height_m'), message: 'Survey (sensor) height must be more than 0 m.' });
  if (a.zone === null || !(a.zone >= 1 && a.zone <= 60))
    issues.push({ field: f('zone'), message: 'UTM zone must be 1 to 60.' });
  if (a.line_km_override !== null && !a.override_reason.trim()) {
    issues.push({
      field: f('override_reason'),
      message: 'Say where the typed line km come from, for example the old quote or contract.',
    });
  }
  const t = a.boundary_table;
  if (t) {
    if (!t.datum.trim()) {
      issues.push({
        field: f('boundary_table.datum'),
        message: 'Choose the datum of the boundary coordinates.',
      });
    }
    if (t.points.length < 3) {
      issues.push({ field: f('boundary_table'), message: 'The boundary table needs at least 3 points.' });
    }
    if (t.kind === 'grid' && (t.zone === null || !(t.zone >= 1 && t.zone <= 60))) {
      issues.push({
        field: f('boundary_table.zone'),
        message: 'Give the grid zone of the boundary coordinates.',
      });
    }
  }
  if (a.traverse_km + a.tie_km === 0 && a.line_km_override === null) {
    issues.push({
      field: f('lines_file'),
      message: "Import the area's lines file (.lns) or flight-plan summary (.sum), or type the line km.",
    });
  }
  // TJR ms 64496 "Missing cost per line KM for Quote item"
  if (pricing && !(a.rate_per_line_km !== null && a.rate_per_line_km > 0)) {
    issues.push({
      field: f('rate_per_line_km'),
      message: `${a.name || 'This area'}: enter the rate per line km.`,
    });
  }
  return issues;
}

/**
 * What must be filled before a revision is sent (TJR's quote print checks, `FUN_006f58a0`, minus what the 2026
 * contract shows is optional: "Located" is optional, bug B4). Grouped by step so the editor can show each step's.
 * The area and charge checks run for every option (decision 171), named by the option when there are several.
 */
export function validateQuoteForSend(agg: QuoteAggregate): ValidationIssue[] {
  const options = optionsOf(agg);
  const issues: ValidationIssue[] = [...validateOptions(agg)];
  const one = options.length === 1;
  for (const o of options) {
    const oa = optionAggregate(agg, o.id);
    const named = (i: ValidationIssue): ValidationIssue =>
      one ? i : { field: i.field, message: `${optionName(o)}: ${i.message}` };
    issues.push(...validateOptionForSend(oa).map(named));
  }
  issues.push(...validateQuoteCommonForSend(agg));
  return issues;
}

/** The checks of one option: its aircraft, areas, rates and charges. */
function validateOptionForSend(agg: QuoteAggregate): ValidationIssue[] {
  const q = agg.quote;
  const issues: ValidationIssue[] = [];
  const need = (ok: boolean, field: string, message: string): void => {
    if (!ok) issues.push({ field, message });
  };
  need(q.aircraft_type.trim() !== '', 'aircraft_type', 'Choose the aircraft type to quote.'); // ms 64509
  need(agg.areas.length > 0, 'areas', 'Add at least one survey area.');
  for (const a of agg.areas) issues.push(...validateArea(a, true));
  const standby = agg.charges.find((c) => c.kind === 'standby');
  need(
    standby !== undefined && standby.amount !== null && standby.amount >= 0,
    'charge.standby',
    'Enter the daily standby rate.',
  );
  const fuel = agg.charges.find((c) => c.kind === 'fuel');
  if (fuel?.fuel_mode === 'per_litre') {
    need(fuel.amount !== null && fuel.amount > 0, 'charge.fuel', 'Enter the fuel cost per litre.'); // ms 64490
  }
  for (const c of agg.charges) {
    if (c.amount !== null && !(Number.isFinite(c.amount) && c.amount >= 0)) {
      issues.push({ field: `charge.${c.kind}`, message: 'Amounts must be zero or more.' });
    }
  }
  if (standby && standby.cap_days !== null && !(Number.isInteger(standby.cap_days) && standby.cap_days > 0)) {
    issues.push({
      field: 'charge.standby.cap_days',
      message: 'Cap standby days must be a whole number of days.',
    });
  }
  return issues;
}

/** The checks of what every option shares: client, job, terms and QC limits. */
function validateQuoteCommonForSend(agg: QuoteAggregate): ValidationIssue[] {
  const q = agg.quote;
  const issues: ValidationIssue[] = [];
  const need = (ok: boolean, field: string, message: string): void => {
    if (!ok) issues.push({ field, message });
  };
  need(q.client_org_id !== null, 'client_org_id', 'Choose the client.');
  need(q.principal_contact_id !== null, 'principal_contact_id', 'Choose the principal client contact.');
  need(
    Boolean(agg.client && (agg.client.address_line1.trim() || agg.client.suburb.trim())),
    'client.address',
    "Enter the client's address on the client record.",
  );
  need(q.project_name.trim() !== '', 'project_name', 'Enter the project name.');
  need(q.location.trim() !== '', 'location', 'Enter the location, for example Western Australia.');
  need(q.country.trim() !== '', 'country', 'Enter the country.');
  need(
    q.bases.some((b) => b.trim() !== ''),
    'bases',
    'Enter at least one proposed base.',
  ); // ms 64532
  need(/^\d{4}-\d{2}$/.test(q.est_start_month), 'est_start_month', 'Choose the estimated start month.');
  need(
    q.est_duration_value !== null && q.est_duration_value > 0,
    'est_duration_value',
    'Enter the estimated job duration.',
  );
  need(q.signatory_name.trim() !== '', 'signatory_name', 'Choose who signs the quote.');
  issues.push(...validatePaymentSchedule(agg.terms));
  issues.push(...validateQcRules(agg.qc));
  return issues;
}

/** Extra checks before a contract is issued (TJR `FUN_006f5fa8`). */
export function validateContractForIssue(agg: QuoteAggregate): ValidationIssue[] {
  const issues = validateQuoteForSend(agg);
  if (agg.quote.delivery_working_days === null || agg.quote.delivery_working_days <= 0) {
    issues.push({ field: 'delivery_working_days', message: 'Enter the delivery working days.' });
  }
  if (!agg.quote.governing_law.trim())
    issues.push({ field: 'governing_law', message: 'Enter the governing law state.' });
  if (agg.quote.notices_contact_id === null && agg.quote.principal_contact_id === null) {
    issues.push({ field: 'notices_contact_id', message: 'Choose the contact for notices.' });
  }
  return issues;
}

/**
 * The frozen contract snapshot: the accepted revision's chosen option (decision 171; the only option of a quote
 * without a choice) plus the clause sections in force today.
 */
export function contractSnapshot(
  accepted: QuoteAggregate,
  clauses: ClauseTemplate,
  aircraftRegos: readonly string[],
  chosenOptionId: Uuid | null = null,
): ContractSnapshot {
  const option = acceptedOptionId(accepted, chosenOptionId);
  const copy = JSON.parse(JSON.stringify(optionAggregate(accepted, option))) as QuoteAggregate;
  return {
    quote: { ...copy, quote: { ...copy.quote, aircraft_regos: [...aircraftRegos] } },
    clauses: JSON.parse(JSON.stringify(clauses)) as ClauseTemplate,
  };
}

export interface ProjectSetup {
  readonly project: {
    readonly number: string;
    readonly title: string;
    readonly client_org_id: Uuid | null;
    readonly client_name: string;
    readonly area: string;
    readonly state: string;
    readonly job_type: 'fixed_wing' | 'helicopter';
    readonly operating_base: string;
    readonly epsg: number | null;
    readonly qc_hand_sample_pct: number | null;
    readonly qc_test_line_pct: number | null;
  };
  readonly blocks: ReadonlyArray<{
    readonly name: string;
    readonly key: string;
    readonly digit: string;
    readonly kind: 'survey';
    readonly epsg: number | null;
    readonly terrain_clearance_m: number;
    readonly trav_spacing_m: number;
    readonly trav_direction_deg: number;
    readonly tie_spacing_m: number;
    readonly tie_direction_deg: number;
    readonly planned_km: number;
    readonly boundary: ReadonlyArray<readonly [number, number]>;
  }>;
  readonly qc_rules: readonly QcRule[];
}

/** WGS 84 / UTM: 327zz south, 326zz north. */
export function utmEpsg(zone: number | null, hemisphere: 'N' | 'S'): number | null {
  if (zone === null || zone < 1 || zone > 60) return null;
  return (hemisphere === 'N' ? 32600 : 32700) + zone;
}

/**
 * Project set-up from a signed contract (decision 119: job number from the quote digits): contracted areas become
 * blocks with spacing, direction, clearance and boundary (block digits 1, 2, 3… in contract order, the TJR block
 * slots); the QC limits are copied, and the thorium limits also go to the project columns Field reads today.
 * Planned lines arrive later from the final flight plan, which replaces the contracted km.
 */
export function projectFromContract(snapshot: ContractSnapshot, jobNumber?: string): ProjectSetup {
  const agg = snapshot.quote;
  const q = agg.quote;
  const number = jobNumber ?? jobNumberFromQuote(q.number, q.job_type) ?? q.number;
  const areas = [...agg.areas].sort((a, b) => a.sort - b.sort);
  const first = areas[0];
  const blocks = areas.slice(0, 9).map((a, i) => ({
    name: a.name,
    key: a.name.trim().replace(/\s+/g, '_'),
    digit: String(i + 1),
    kind: 'survey' as const,
    epsg: utmEpsg(a.zone, a.hemisphere),
    terrain_clearance_m: a.sensor_height_m,
    trav_spacing_m: a.trav_spacing_m,
    trav_direction_deg: a.trav_direction_deg,
    tie_spacing_m: a.tie_spacing_m,
    tie_direction_deg: a.tie_direction_deg,
    planned_km: areaLineKm(a),
    boundary: a.boundary,
  }));
  return {
    project: {
      number,
      title: q.project_name,
      client_org_id: q.client_org_id,
      client_name: agg.client?.name ?? '',
      area: q.location,
      state: q.state,
      job_type: q.job_type,
      operating_base: q.bases[0] ?? '',
      epsg: first ? utmEpsg(first.zone, first.hemisphere) : null,
      ...projectQcColumns(agg.qc),
    },
    blocks,
    qc_rules: agg.qc,
  };
}

/** A project holds nine block slots (TJR); more areas need a decision before set-up. */
export function validateProjectSetup(snapshot: ContractSnapshot): ValidationIssue[] {
  const n = snapshot.quote.areas.length;
  if (n === 0) return [{ field: 'areas', message: 'The contract has no survey areas.' }];
  if (n > 9)
    return [{ field: 'areas', message: `The contract has ${n} areas; a project holds up to 9 blocks.` }];
  return [];
}
